import { useMemo } from 'react';
import { LayerGroup, LayersControl } from 'react-leaflet';
import { ObjectModel } from '@customer/store/apis/interactionsApi';
import ObjectMarker, { ObjectMarkerProps, ObjectType } from '../markers/object-marker/ObjectMarker';
import { isPositionModelWithLatLng, PositionModelRelative } from '@customer/store/apis/assetsApi';
import { getRotationProps } from '../markers/utils';
import { useTranslation } from 'react-i18next';
import { RelativeInteractionLayer } from '../relative-interaction-layer/RelativeInteractionLayer';
import { LatLngExpression } from 'leaflet';

interface InteractionObjectsProps {
  lo: ObjectModel;
  ro?: ObjectModel | null;
}

interface RemoteRelativeProps {
  loPosition: LatLngExpression;
  loHeading: number | null;
  roPositon: PositionModelRelative;
  name: string;
}

interface LayersData {
  lo?: ObjectMarkerProps;
  roAccurate?: ObjectMarkerProps;
  roRelative?: RemoteRelativeProps;
}

export function InteractionObjects({ lo, ro }: InteractionObjectsProps) {
  const { t } = useTranslation();

  const layerData = useMemo<LayersData>(() => {
    const layerData: LayersData = {};

    if (isPositionModelWithLatLng(lo.startPos)) {
      layerData.lo = {
        ...getRotationProps(lo.startPos),
        objectType: ObjectType.LO,
        name: lo.n,
      };
    }

    if (isPositionModelWithLatLng(ro?.startPos)) {
      layerData.roAccurate = {
        ...getRotationProps(ro.startPos),
        objectType: ObjectType.RO,
        name: ro.n,
      };
    } else if (isPositionModelWithLatLng(lo.startPos) && ro?.startPos?.rel) {
      // This is the DotNetix "person" scenario. The RO position is relative to the LO's position
      const { position, heading } = getRotationProps(lo.startPos);

      layerData.roRelative = {
        loHeading: heading,
        loPosition: position,
        roPositon: ro.startPos.rel,
        name: t('ObjectDetection'),
      };
    }

    return layerData;
  }, [lo.startPos, lo.n, ro?.startPos, ro?.n, t]);

  return (
    <>
      {layerData.lo && (
        <LayersControl.Overlay key="lo" name={layerData.lo.name} checked={true}>
          <LayerGroup>
            <ObjectMarker key={layerData.lo.name} {...layerData.lo} />
          </LayerGroup>
        </LayersControl.Overlay>
      )}

      {layerData.roAccurate && (
        <LayersControl.Overlay key="roAccurate" name={layerData.roAccurate.name} checked={true}>
          <LayerGroup>
            <ObjectMarker key={layerData.roAccurate.name} {...layerData.roAccurate} />
          </LayerGroup>
        </LayersControl.Overlay>
      )}

      {layerData.roRelative && (
        <LayersControl.Overlay key="roRelative" name={layerData.roRelative.name} checked={true}>
          <LayerGroup>
            <RelativeInteractionLayer
              position={layerData.roRelative.loPosition}
              heading={layerData.roRelative.loHeading}
              relativePosition={layerData.roRelative.roPositon}
            />
          </LayerGroup>
        </LayersControl.Overlay>
      )}
    </>
  );
}

export default InteractionObjects;
